import { useMemo } from 'react';

import { useTheme } from '@material-ui/core';

import {
  ColorCategory,
  DARK_BG_COLORS,
  DARK_TEXT_COLORS,
  LIGHT_BG_COLORS,
  LIGHT_TEXT_COLORS,
  SEMANTIC_COLORS,
  SemanticColor,
} from './colorDefinitions';

type ColorMap = Record<SemanticColor, string>;

const getColorMap = (
  category: ColorCategory,
  isDarkTheme: boolean,
): ColorMap => {
  if (category === 'text') {
    return isDarkTheme ? DARK_TEXT_COLORS : LIGHT_TEXT_COLORS;
  }
  return isDarkTheme ? DARK_BG_COLORS : LIGHT_BG_COLORS;
};

const isValidSemanticColor = (name: string): name is SemanticColor => {
  return SEMANTIC_COLORS.includes(name as SemanticColor);
};

export const resolveSemanticColor = (
  semanticName: SemanticColor | null | undefined,
  category: ColorCategory,
  isDarkTheme: boolean,
): string | null => {
  if (!semanticName) return null;

  if (!isValidSemanticColor(semanticName)) {
    return null;
  }

  const colorMap = getColorMap(category, isDarkTheme);
  return colorMap[semanticName];
};

export const useColorResolver = () => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.type === 'dark';

  return useMemo(
    () => ({
      resolveTextColor: (name: SemanticColor | null | undefined) =>
        resolveSemanticColor(name, 'text', isDarkTheme),
      resolveBackgroundColor: (name: SemanticColor | null | undefined) =>
        resolveSemanticColor(name, 'background', isDarkTheme),
      isDarkTheme,
    }),
    [isDarkTheme],
  );
};

export const getColorByClass = (colorClass: string): string => {
  if (colorClass === 'good') return '#1AA84C';
  if (colorClass === 'bad') return '#EF2A3D';
  if (colorClass === 'warn') return 'var(--bui-fg-warning)';

  return '#707070';
};

export const generateSemanticColorStyles = (isDarkTheme: boolean) => {
  const styles: Record<string, Record<string, string>> = {};

  SEMANTIC_COLORS.forEach((colorName: SemanticColor) => {
    const textColor = resolveSemanticColor(colorName, 'text', isDarkTheme);
    const bgColor = resolveSemanticColor(colorName, 'background', isDarkTheme);

    if (textColor) {
      styles[`& span[data-semantic-color="${colorName}"]`] = {
        color: textColor,
      };
    }
    if (bgColor) {
      styles[`& mark[data-semantic-highlight="${colorName}"]`] = {
        backgroundColor: bgColor,
        borderRadius: 'var(--bui-radius-1)',
        padding: '0 2px',
        color: 'inherit',
      };
    }
  });

  return styles;
};
